import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { packagesService } from "services/packages.service";
import { Package } from "types/package";
import { queryKeys } from "lib/queryKeys";

interface PackagesContextValue {
  packages: Package[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<Package[]>;
}

const PackagesContext = createContext<PackagesContextValue | null>(null);

async function fetchAllPackages(): Promise<Package[]> {
  const data = await packagesService.getAll();
  return Array.isArray(data) ? data : [];
}

export const PackagesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.packages.all,
    queryFn: fetchAllPackages,
    staleTime: 60_000,
  });

  const refresh = useCallback(async () => {
    const result = await refetch();
    return result.data ?? [];
  }, [refetch]);

  const value = useMemo(
    () => ({
      packages: data ?? [],
      isLoading,
      error: error instanceof Error ? error.message : error ? String(error) : null,
      refresh,
    }),
    [data, isLoading, error, refresh]
  );

  return (
    <PackagesContext.Provider value={value}>{children}</PackagesContext.Provider>
  );
};

export const usePackages = (): PackagesContextValue => {
  const ctx = useContext(PackagesContext);
  if (!ctx) {
    throw new Error("usePackages must be used within PackagesProvider");
  }
  return ctx;
};

/** Safe outside provider — one-off query via shared QueryClient when mounted under provider elsewhere */
export const usePackagesOptional = (): PackagesContextValue => {
  const ctx = useContext(PackagesContext);
  const queryClient = useQueryClient();

  const fallback = useQuery({
    queryKey: queryKeys.packages.all,
    queryFn: fetchAllPackages,
    enabled: !ctx,
    staleTime: 60_000,
  });

  if (ctx) return ctx;

  return {
    packages: fallback.data ?? [],
    isLoading: fallback.isLoading,
    error:
      fallback.error instanceof Error
        ? fallback.error.message
        : fallback.error
          ? String(fallback.error)
          : null,
    refresh: async () => {
      const result = await queryClient.fetchQuery({
        queryKey: queryKeys.packages.all,
        queryFn: fetchAllPackages,
      });
      return result;
    },
  };
};
